/**
 * Client sync engine (FLASH-30, FR-7 / SDD §2.7) — the offline study core.
 *
 * Imports the SAME fold the StudySessionDO uses (src/do/fold.ts, EVENTS.md §9:
 * one fold implementation, two replicas). Reviews are recorded as §1-shaped
 * append-only events into an injected store, folded locally with ts-fsrs for
 * instant feedback (NFR-1), queued in an outbox, and pushed to
 * POST /v1/study/reviews on reconnect. Acks adopt the frozen server_recv_ts
 * onto the local events (§1.2 MUST) and the touched cards refold under the
 * frozen stamps — after which device and DO state are byte-identical for the
 * shared event set (proven by test/client-sync.test.ts against the real DO).
 *
 * Pre-ack, a local event folds under an OPTIMISTIC stamp (local receipt time):
 * the §4 upper clamp is a no-op for the device's own un-skewed clock, and the
 * post-ack refold replaces it with the frozen value, converging with the DO —
 * the same model test/study-convergence.test.ts proves over 100 random runs.
 *
 * Anchors are frozen write-once locally (§6.2): an explicit card_created_ts
 * wins; otherwise the first local fold pins the earliest local event ts.
 * Adopting the DO's anchor for cards first seen elsewhere needs the event-pull
 * surface (FLASH-43); until then cards studied on multiple devices converge
 * once the pull lands — single-device offline study (the FR-7 P0) is exact.
 *
 * V0 boundaries (documented, tracked): params sync-down + per-card import
 * param versions fold server-side only — when a pushed snapshot reports a
 * params_version this engine doesn't hold, the engine ADOPTS the server
 * snapshot verbatim instead of trusting its local fold (§7.1 rule 3) and
 * flags the card server-authoritative until a pull refreshes params.
 */
import { default_w, default_request_retention } from "ts-fsrs";
import {
  makeScheduler,
  foldCard,
  type FsrsParams,
  type FoldEvent,
  type FoldedCard,
} from "../do/fold";
import type {
  CardStateSnapshot,
  ReviewEventInput,
  ReviewSubmitResponse,
  ReviewGrade,
  ReviewSource,
  StudyPullResponse,
  FsrsParamsRow,
} from "../shared/types";

/** A locally-stored review event: the §1 wire shape plus sync bookkeeping. */
export interface StoredReviewEvent {
  event_id: string;
  card_id: string;
  ts: number;
  grade: ReviewGrade;
  elapsed_ms: number;
  source: ReviewSource;
  card_created_ts?: number;
  /** Frozen server stamp once acked; optimistic local receipt time before. */
  server_recv_ts: number;
  /** True once an ack adopted the frozen stamp (event left the outbox). */
  acked: boolean;
}

/**
 * Storage seam: IndexedDB in the browser (src/client/indexeddb-store.ts),
 * in-memory for tests and as the reference implementation. All methods are
 * async so any backend fits; the engine never assumes synchronous storage.
 */
export interface SyncStore {
  appendEvent(e: StoredReviewEvent): Promise<void>;
  /** Upsert by event_id (stamp adoption / ack marking / pull adoption). */
  putEvents(events: StoredReviewEvent[]): Promise<void>;
  /** Delete an event only while it is still unacked. Used by the short undo
   *  grace period before an event is eligible to leave the device. */
  deleteUnackedEvent(eventId: string): Promise<boolean>;
  eventsForCard(cardId: string): Promise<StoredReviewEvent[]>;
  /** Oldest-first unacked events (the outbox view). */
  unacked(limit: number): Promise<StoredReviewEvent[]>;
  getAnchor(cardId: string): Promise<number | undefined>;
  /** Write-once by contract — the engine never overwrites an existing anchor. */
  setAnchor(cardId: string, createdTs: number): Promise<void>;
  /** AUTHORITATIVE overwrite: the DO's frozen anchor wins over a provisional
   *  local pin (§6.2 — client and DO must hold the same frozen value). */
  adoptAnchor(cardId: string, createdTs: number): Promise<void>;
  /** Cards whose local fold must not be trusted until a params pull (§7.1). */
  getServerAuthoritative(cardId: string): Promise<CardStateSnapshot | undefined>;
  setServerAuthoritative(snapshot: CardStateSnapshot): Promise<void>;
  deleteServerAuthoritative(cardId: string): Promise<void>;
  /** Pull cursor (EVENTS.md §1.5 next_seq). */
  getCursor(): Promise<number>;
  setCursor(seq: number): Promise<void>;
  /** Param registry + per-card bindings adopted from pulls (§7.2). */
  putParamsRow(row: FsrsParamsRow): Promise<void>;
  getParamsRow(version: number): Promise<FsrsParamsRow | undefined>;
  putBinding(cardId: string, version: number): Promise<void>;
  getBinding(cardId: string): Promise<number | undefined>;
}

/** Reference in-memory store (tests, SSR-less previews). */
export class MemorySyncStore implements SyncStore {
  private events = new Map<string, StoredReviewEvent>();
  private anchors = new Map<string, number>();
  private serverAuth = new Map<string, CardStateSnapshot>();
  private paramsRows = new Map<number, FsrsParamsRow>();
  private bindings = new Map<string, number>();
  private cursor = 0;

  async appendEvent(e: StoredReviewEvent): Promise<void> {
    this.events.set(e.event_id, e);
  }
  async putEvents(events: StoredReviewEvent[]): Promise<void> {
    for (const e of events) this.events.set(e.event_id, e);
  }
  async deleteUnackedEvent(eventId: string): Promise<boolean> {
    const event = this.events.get(eventId);
    if (!event || event.acked) return false;
    return this.events.delete(eventId);
  }
  async eventsForCard(cardId: string): Promise<StoredReviewEvent[]> {
    return [...this.events.values()].filter((e) => e.card_id === cardId);
  }
  async unacked(limit: number): Promise<StoredReviewEvent[]> {
    return [...this.events.values()]
      .filter((e) => !e.acked)
      .sort((a, b) => a.ts - b.ts)
      .slice(0, limit);
  }
  async getAnchor(cardId: string): Promise<number | undefined> {
    return this.anchors.get(cardId);
  }
  async setAnchor(cardId: string, createdTs: number): Promise<void> {
    if (!this.anchors.has(cardId)) this.anchors.set(cardId, createdTs);
  }
  async adoptAnchor(cardId: string, createdTs: number): Promise<void> {
    this.anchors.set(cardId, createdTs);
  }
  async getServerAuthoritative(cardId: string): Promise<CardStateSnapshot | undefined> {
    return this.serverAuth.get(cardId);
  }
  async setServerAuthoritative(snapshot: CardStateSnapshot): Promise<void> {
    this.serverAuth.set(snapshot.card_id, snapshot);
  }
  async deleteServerAuthoritative(cardId: string): Promise<void> {
    this.serverAuth.delete(cardId);
  }
  async getCursor(): Promise<number> {
    return this.cursor;
  }
  async setCursor(seq: number): Promise<void> {
    this.cursor = seq;
  }
  async putParamsRow(row: FsrsParamsRow): Promise<void> {
    this.paramsRows.set(row.version, row);
  }
  async getParamsRow(version: number): Promise<FsrsParamsRow | undefined> {
    return this.paramsRows.get(version);
  }
  async putBinding(cardId: string, version: number): Promise<void> {
    this.bindings.set(cardId, version);
  }
  async getBinding(cardId: string): Promise<number | undefined> {
    return this.bindings.get(cardId);
  }
}

/** EVENTS.md §5.2 / route cap: max events per push batch. */
export const MAX_PUSH_BATCH = 500;

/** Transport seam: the caller wires fetch + auth + the Idempotency-Key header. */
export type PushTransport = (
  body: { events: ReviewEventInput[] },
  idempotencyKey: string,
) => Promise<ReviewSubmitResponse>;

export interface PushReport {
  pushed: number;
  duplicates: number;
  /** Cards adopted verbatim from the server (unknown params_version, §7.1). */
  serverAuthoritative: string[];
}

export interface RecordReviewInput {
  /** Caller-supplied id lets the UI identify the exact local event for undo. */
  event_id?: string;
  card_id: string;
  grade: ReviewGrade;
  elapsed_ms: number;
  source?: ReviewSource;
  /** Override for tests/imports; defaults to the engine clock. */
  ts?: number;
  /** Explicit creation anchor (frozen write-once, §6.2). */
  card_created_ts?: number;
}

/** Pull transport seam (GET /v1/study/pull behind auth, EVENTS.md §1.5). */
export type PullTransport = (sinceSeq: number, limit?: number) => Promise<StudyPullResponse>;

export interface PullReport {
  /** Events adopted (foreign + own-confirmed). */
  events: number;
  pages: number;
  cursor: number;
}

export class SyncEngine {
  private readonly scheduler;
  /** Serialize ack adoption and undo so a review cannot be removed while its
   * push or pull is in flight and then resurrected as an acknowledged event. */
  private eventMutationTail: Promise<void> = Promise.resolve();
  /** One scheduler per param version (§7.1) — built lazily from pulled rows. */
  private readonly schedulerCache = new Map<number, ReturnType<typeof makeScheduler>>();

  constructor(
    private readonly store: SyncStore,
    private readonly params: FsrsParams = defaultClientParams(),
    private readonly now: () => number = () => Date.now(),
    private readonly uuid: () => string = () => crypto.randomUUID(),
  ) {
    this.scheduler = makeScheduler(this.params);
    this.schedulerCache.set(this.params.version, this.scheduler);
  }

  private async serializeEventMutation<T>(action: () => Promise<T>): Promise<T> {
    const previous = this.eventMutationTail;
    let release!: () => void;
    this.eventMutationTail = new Promise<void>((resolve) => { release = resolve; });
    await previous;
    try {
      return await action();
    } finally {
      release();
    }
  }

  /**
   * Resolve the scheduler + version for one card (§7.2): bound version when we
   * hold its weights (pulled registry), else the user params. A bound card
   * whose vector we have NOT pulled folds under user params locally — push/pull
   * adoption keeps it server-authoritative until the registry arrives.
   */
  private async schedulerForCard(
    cardId: string,
  ): Promise<{ scheduler: ReturnType<typeof makeScheduler>; version: number }> {
    const bound = await this.store.getBinding(cardId);
    if (bound === undefined || bound === this.params.version) {
      return { scheduler: this.scheduler, version: this.params.version };
    }
    const cached = this.schedulerCache.get(bound);
    if (cached) return { scheduler: cached, version: bound };
    const row = await this.store.getParamsRow(bound);
    if (!row) return { scheduler: this.scheduler, version: this.params.version };
    const built = makeScheduler({
      w: row.w,
      request_retention: row.request_retention,
      version: row.version,
      updated_at: row.updated_at,
    });
    this.schedulerCache.set(bound, built);
    return { scheduler: built, version: bound };
  }

  /** Record a graded review offline: append event, refold, return the snapshot. */
  async recordReview(input: RecordReviewInput): Promise<CardStateSnapshot> {
    const ts = input.ts ?? this.now();
    const event: StoredReviewEvent = {
      event_id: input.event_id ?? this.uuid(),
      card_id: input.card_id,
      ts,
      grade: input.grade,
      elapsed_ms: input.elapsed_ms,
      source: input.source ?? "review",
      ...(input.card_created_ts !== undefined ? { card_created_ts: input.card_created_ts } : {}),
      server_recv_ts: this.now(), // optimistic; replaced by the frozen ack stamp
      acked: false,
    };
    await this.store.appendEvent(event);
    if (input.card_created_ts !== undefined) {
      await this.store.setAnchor(input.card_id, input.card_created_ts);
    }
    return this.foldCardLocal(input.card_id);
  }

  /**
   * Undo one review while it is still local. Acked events are immutable, so
   * callers should keep a brief push grace period while the Undo affordance is
   * active. Returns the refolded state (or null for a card back at NEW).
   */
  async undoReview(
    eventId: string,
    cardId: string,
  ): Promise<{ undone: boolean; state: CardStateSnapshot | null }> {
    return this.serializeEventMutation(async () => {
      const undone = await this.store.deleteUnackedEvent(eventId);
      if (!undone) return { undone: false, state: await this.stateFor(cardId) };
      return { undone: true, state: await this.stateFor(cardId) };
    });
  }

  /** Number of reviews currently waiting to leave this device. */
  async pendingCount(): Promise<number> {
    return (await this.store.unacked(MAX_PUSH_BATCH)).length;
  }

  /** Current local state for a card (null before its first review). */
  async stateFor(cardId: string): Promise<CardStateSnapshot | null> {
    const serverAuth = await this.store.getServerAuthoritative(cardId);
    if (serverAuth) return serverAuth;
    const events = await this.store.eventsForCard(cardId);
    if (events.length === 0) return null;
    return this.foldCardLocal(cardId);
  }

  /**
   * Per-grade due preview for the grade bar (FR-5.2 "predicted intervals").
   * Pure preview — folds a copy, records nothing.
   */
  async predictDue(cardId: string, at = this.now()): Promise<Record<ReviewGrade, number>> {
    const events = await this.store.eventsForCard(cardId);
    const anchor = await this.resolveAnchor(cardId, events, at);
    const { scheduler } = await this.schedulerForCard(cardId);
    const history = toFoldEvents(events);
    const out = {} as Record<ReviewGrade, number>;
    for (const grade of [1, 2, 3, 4] as const) {
      const preview = foldCard(scheduler, cardId, anchor, [
        ...history,
        {
          event_id: `00000000-0000-4000-8000-00000000000${grade}`,
          card_id: cardId,
          ts: at,
          grade,
          source: "review",
          server_recv_ts: at,
        },
      ]);
      out[grade] = preview.due_ts;
    }
    return out;
  }

  /**
   * Push the outbox (≤500 events) and adopt the response: frozen stamps onto
   * local events (§1.2), refold touched cards, server-snapshot adoption for
   * unknown param versions (§7.1). Re-pushing after a lost ack is safe — the
   * DO acks duplicates with the ORIGINAL stamp, and the deterministic
   * Idempotency-Key replays the cached response for an identical batch.
   */
  async push(transport: PushTransport): Promise<PushReport> {
    return this.serializeEventMutation(() => this.pushUnserialized(transport));
  }

  private async pushUnserialized(transport: PushTransport): Promise<PushReport> {
    const batch = await this.store.unacked(MAX_PUSH_BATCH);
    if (batch.length === 0) return { pushed: 0, duplicates: 0, serverAuthoritative: [] };

    const events: ReviewEventInput[] = batch.map((e) => ({
      event_id: e.event_id,
      card_id: e.card_id,
      ts: e.ts,
      grade: e.grade,
      elapsed_ms: e.elapsed_ms,
      source: e.source,
      ...(e.card_created_ts !== undefined ? { card_created_ts: e.card_created_ts } : {}),
    }));
    // Deterministic per-batch key: a retry of the same batch replays the
    // cached route response instead of re-ingesting (EVENTS.md §2.2).
    const idempotencyKey = `outbox-${batch[0]!.event_id}-${batch.length}`;

    const res = await transport({ events }, idempotencyKey);

    const byId = new Map(batch.map((e) => [e.event_id, e]));
    let duplicates = 0;
    const adopted: StoredReviewEvent[] = [];
    for (const ack of res.accepted) {
      const local = byId.get(ack.event_id);
      if (!local) continue;
      if (ack.duplicate) duplicates++;
      adopted.push({ ...local, server_recv_ts: ack.server_recv_ts, acked: true });
    }
    await this.store.putEvents(adopted);

    // §7.1: a snapshot folded under a params version we don't hold is adopted
    // verbatim — never refold under a stale local `w`.
    const serverAuthoritative: string[] = [];
    for (const snap of res.card_states) {
      if (snap.params_version !== this.params.version) {
        await this.store.setServerAuthoritative(snap);
        serverAuthoritative.push(snap.card_id);
      }
    }

    return { pushed: adopted.length - duplicates, duplicates, serverAuthoritative };
  }

  /**
   * Pull the event union from the DO (EVENTS.md §1.5) and adopt everything a
   * faithful replica needs: foreign events WITH their frozen stamps, the DO's
   * frozen anchors (authoritative — they overwrite a provisional local pin,
   * §6.2), the param registry + bindings (§7.2 — bound cards then fold locally
   * under the right weights), and the resume cursor. Pulling our own pushed
   * events back is harmless: the upsert confirms their stamps. Pages until
   * has_more is false (bounded), so call it on reconnect/foreground.
   */
  async pull(transport: PullTransport, maxPages = 20): Promise<PullReport> {
    return this.serializeEventMutation(() => this.pullUnserialized(transport, maxPages));
  }

  private async pullUnserialized(transport: PullTransport, maxPages: number): Promise<PullReport> {
    let cursor = await this.store.getCursor();
    let events = 0;
    let pages = 0;

    while (pages < maxPages) {
      const page = await transport(cursor);
      pages++;

      for (const row of page.params) await this.store.putParamsRow(row);
      for (const [cardId, version] of Object.entries(page.bindings)) {
        await this.store.putBinding(cardId, version);
      }
      for (const [cardId, createdTs] of Object.entries(page.anchors)) {
        await this.store.adoptAnchor(cardId, createdTs);
      }

      if (page.events.length > 0) {
        await this.store.putEvents(
          page.events.map((e) => ({
            event_id: e.event_id,
            card_id: e.card_id,
            ts: e.ts,
            grade: e.grade as ReviewGrade,
            elapsed_ms: e.elapsed_ms,
            source: e.source,
            server_recv_ts: e.server_recv_ts,
            acked: true,
          })),
        );
        events += page.events.length;

        // A card we previously held only as a server snapshot can fold locally
        // once its bound version's weights are in the registry.
        for (const cardId of new Set(page.events.map((e) => e.card_id))) {
          const snap = await this.store.getServerAuthoritative(cardId);
          if (!snap) continue;
          const bound = await this.store.getBinding(cardId);
          const known =
            snap.params_version === this.params.version ||
            (bound !== undefined && (await this.store.getParamsRow(snap.params_version)));
          if (known) await this.store.deleteServerAuthoritative(cardId);
        }
      }

      cursor = page.next_seq;
      await this.store.setCursor(cursor);
      if (!page.has_more) break;
    }

    return { events, pages, cursor };
  }

  private async foldCardLocal(cardId: string): Promise<CardStateSnapshot> {
    const events = await this.store.eventsForCard(cardId);
    const anchor = await this.resolveAnchor(cardId, events, this.now());
    const { scheduler, version } = await this.schedulerForCard(cardId);
    const folded: FoldedCard = foldCard(scheduler, cardId, anchor, toFoldEvents(events));
    return { ...folded, params_version: version };
  }

  /** Frozen write-once anchor (§6.2): explicit wins, else first-fold pin. */
  private async resolveAnchor(
    cardId: string,
    events: StoredReviewEvent[],
    fallback: number,
  ): Promise<number> {
    const existing = await this.store.getAnchor(cardId);
    if (existing !== undefined) return existing;
    const earliest = events.reduce((m, e) => Math.min(m, e.ts), Number.POSITIVE_INFINITY);
    const anchor = Number.isFinite(earliest) ? earliest : fallback;
    await this.store.setAnchor(cardId, anchor);
    return anchor;
  }
}

function toFoldEvents(events: StoredReviewEvent[]): FoldEvent[] {
  return events.map((e) => ({
    event_id: e.event_id,
    card_id: e.card_id,
    ts: e.ts,
    grade: e.grade,
    source: e.source,
    server_recv_ts: e.server_recv_ts,
  }));
}

/** Seed params matching the DO's version-1 defaults (EVENTS.md §7). */
export function defaultClientParams(): FsrsParams {
  return {
    w: [...default_w],
    request_retention: default_request_retention,
    version: 1,
    updated_at: 0,
  };
}
